import { Injectable, NgZone, inject, signal } from "@angular/core";
import { io, Socket } from "socket.io-client";
import { APP_CONSTANTS } from "../../../core/constants/app.constants";
import type { VideoMatch } from "../models/video.models";

@Injectable()
export class VideoRealtimeService {
  private socket?: Socket;
  private readonly zone = inject(NgZone);

  readonly connected = signal(false);
  readonly matchStatus = signal<"idle" | "waiting">("idle");
  readonly match = signal<VideoMatch | null>(null);
  readonly roomId = signal<string | null>(null);
  readonly incomingOffer = signal<RTCSessionDescriptionInit | null>(null);
  readonly incomingAnswer = signal<RTCSessionDescriptionInit | null>(null);
  readonly incomingIceCandidate = signal<RTCIceCandidateInit | null>(null);
  readonly peerEvent = signal<"skipped" | "left" | null>(null);

  connect(
    userId: string,
    displayName: string,
    country: string,
    avatarUrl: string | null,
  ): void {
    this.socket?.disconnect();
    this.socket = io(APP_CONSTANTS.videoSocketUrl, {
      transports: ["websocket"],
      auth: { userId, displayName, avatarUrl: avatarUrl ?? "", country },
    });

    this.socket.on("connect", () =>
      this.zone.run(() => this.connected.set(true)),
    );
    this.socket.on("disconnect", () =>
      this.zone.run(() => {
        this.connected.set(false);
        this.matchStatus.set("idle");
      }),
    );

    this.socket.on("video:waiting", () =>
      this.zone.run(() => this.matchStatus.set("waiting")),
    );

    this.socket.on("video:found", (data: VideoMatch) =>
      this.zone.run(() => {
        this.match.set(data);
        this.roomId.set(data.roomId);
        this.matchStatus.set("idle");
        this.peerEvent.set(null);
      }),
    );

    this.socket.on("video:offer", (data: { sdp: RTCSessionDescriptionInit }) =>
      this.zone.run(() => this.incomingOffer.set(data.sdp)),
    );

    this.socket.on("video:answer", (data: { sdp: RTCSessionDescriptionInit }) =>
      this.zone.run(() => this.incomingAnswer.set(data.sdp)),
    );

    this.socket.on(
      "video:ice-candidate",
      (data: { candidate: RTCIceCandidateInit }) =>
        this.zone.run(() => this.incomingIceCandidate.set(data.candidate)),
    );

    this.socket.on("video:peer-skipped", () =>
      this.zone.run(() => {
        this.match.set(null);
        this.roomId.set(null);
        this.peerEvent.set("skipped");
      }),
    );

    this.socket.on("video:peer-left", () =>
      this.zone.run(() => {
        this.match.set(null);
        this.roomId.set(null);
        this.peerEvent.set("left");
      }),
    );

    this.socket.on("video:cancelled", () =>
      this.zone.run(() => this.matchStatus.set("idle")),
    );
  }

  join(interests: string[]): void {
    this.peerEvent.set(null);
    this.incomingOffer.set(null);
    this.incomingAnswer.set(null);
    this.incomingIceCandidate.set(null);
    this.matchStatus.set("waiting");
    this.socket?.emit("video:join", { interests });
  }

  cancel(): void {
    this.matchStatus.set("idle");
    this.socket?.emit("video:cancel");
  }

  skip(roomId: string, interests: string[]): void {
    this.match.set(null);
    this.roomId.set(null);
    this.incomingOffer.set(null);
    this.incomingAnswer.set(null);
    this.socket?.emit("video:skip", { roomId, interests });
  }

  sendOffer(roomId: string, sdp: RTCSessionDescriptionInit): void {
    this.socket?.emit("video:offer", { roomId, sdp });
  }

  sendAnswer(roomId: string, sdp: RTCSessionDescriptionInit): void {
    this.socket?.emit("video:answer", { roomId, sdp });
  }

  sendIceCandidate(roomId: string, candidate: RTCIceCandidateInit): void {
    this.socket?.emit("video:ice-candidate", { roomId, candidate });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = undefined;
    this.connected.set(false);
    this.matchStatus.set("idle");
    this.match.set(null);
    this.roomId.set(null);
    this.incomingOffer.set(null);
    this.incomingAnswer.set(null);
    this.incomingIceCandidate.set(null);
    this.peerEvent.set(null);
  }
}
